import React, { useState, useRef } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import _ from "lodash";

// Zustand store
import { useAppStore } from "../../stores";

// Import Generic components
import {
  saveAttendee,
  loadAttendee,
  loadCurrentUser,
  loadAttendeeByAttendeeId,
} from "./services";

import { getAllCredentialTypes } from "../../actions/CredentialActions/CredentialsActions";

import { useSnackbar } from "notistack";

// import generic components
import CommitteeSelect from "components/CommitteeSelect/CommitteeSelect";
import AlternatePriority from "components/AlternatePriority/AlternatePriority";
import ConventionRegistrationForm from "components/ConventionRegistrationForm/ConventionRegistrationForm";
import GenericSpinner from "../../components/GenericSpinner/GenericSpinner";

// import permissions enum
import { usertype_permissions } from "../../enums/PermissionEnums";
import Dropdown from "components/Dropdown/Dropdown";
import { getAttendeeNotificationInformation } from "../../actions/UserAction/UserActions";
import axios from "axios";

interface Props {
  member?: unknown;
  onRowClick?: (data: unknown, row: unknown) => void;
}

// Custom styles
const regloadingStyle = {
  position: "fixed" as const,
  zIndex: 500,
  top: 0,
  backgroundColor: "rgba(0,0,0, .25)",
  left: 0,
  height: "100%",
  width: "100%",
};

const DelegateRegistration: React.FC<Props> = () => {
  const { enqueueSnackbar } = useSnackbar();

  const location = useLocation();
  const params = useParams();

  // Zustand store
  const activeConvention = useAppStore(
    (state) => state.conventionReducer?.activeConvention
  );
  const email = useAppStore((state) => state.user?.user?.username || "");
  const permissions = useAppStore((state) => state.user?.permissions || []);
  const creds = useAppStore(
    (state) => state.credentials?.credentialTypes || []
  );
  const setCredentialTypes = useAppStore((state) => state.setCredentialTypes);
  const setCommitteeTypes = useAppStore((state) => state.setCommitteeTypes);
  const committeeTypes = useAppStore(
    (state) => state.committees?.committeeTypes || []
  );
  const attendeeSavePending = useAppStore(
    (state) => state.attendee?.attendeeSavePending || false
  );
  const attendeeLoadPending = useAppStore(
    (state) => state.attendee?.attendeeLoadPending || false
  );
  const saveAttendeeError = useAppStore(
    (state) => state.attendee?.attendeeError
  );
  const setSaveAttendeePending = useAppStore((state) => state.setSaveAttendeePending);
  const setSaveAttendeeSuccess = useAppStore((state) => state.setSaveAttendeeSuccess);
  const setSaveAttendeeError = useAppStore((state) => state.setSaveAttendeeError);
  const setLoadAttendeePending = useAppStore((state) => state.setLoadAttendeePending);
  const setLoadAttendeeSuccess = useAppStore((state) => state.setLoadAttendeeSuccess);
  const setLoadAttendeeError = useAppStore((state) => state.setLoadAttendeeError);

  const isAdmin =
    _.find(permissions, (permission: any) => {
      return (
        permission.name.indexOf(usertype_permissions.Admin) !== -1 ||
        permission.name.indexOf(usertype_permissions.BackOffice) !== -1 ||
        permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1 ||
        permission.name.indexOf(usertype_permissions.StateFed) !== -1 ||
        permission.name.indexOf(usertype_permissions.Designee) !== -1
      );
    }) !== undefined;

  const isFullAdmin =
    _.find(permissions, (permission: any) => {
      return (
        permission.name.indexOf(usertype_permissions.Admin) !== -1 ||
        permission.name.indexOf(usertype_permissions.BackOffice) !== -1
      );
    }) !== undefined;

  const isAffiliateStaff =
    _.find(permissions, (permission: any) => {
      return (
        permission.name.indexOf(usertype_permissions.AffiliateStaff) !== -1
      );
    }) !== undefined;

  const saveAttendeeForUserCard = (responseData: any) => {
    if (_.get(responseData, "attendeeId") != 0) {
      console.log('saveAttendeeForUserCard' + responseData.confirmationNumber)
      // Always set credentialType from the list based on credentialTypeId to ensure consistency
      const credType = _.find(creds, function (cred: any) {
        return (
          String(cred.credentialTypeId) ===
          String(responseData.credentialTypeId)
        );
      });
      responseData.credentialType = credType ? (credType as any).label : (responseData.credentialType ?? "");
    }
    // Return the enriched data
    return responseData;
  };

  const [isGuestRecord, setIsGuestRecord] = React.useState(false);
  const [attendeeLoadFailed, setAttendeeLoadFailed] = React.useState(false);

  const [isNewReservation, setIsNewReservation] = React.useState(false);

  const [
    notificationRequestPending,
    setNotificationRequestPending,
  ] = React.useState(false);

  const [
    isRegisteredForConvention,
    setIsRegisteredForConvention,
  ] = React.useState(false);

  const [hasHotelReservation, setHasHotelReservation] = React.useState(false);

  const submitRequestForNotifications = (attendeeId: number) => {
    setNotificationRequestPending(true);
    getAttendeeNotificationInformation(attendeeId)
      .then((response: any) => {
        const { data } = response;
        setIsRegisteredForConvention(data.attendeeExists);
        setHasHotelReservation(data.hotelReservationExists);
        setNotificationRequestPending(false);
      })
      .catch((error) => {
        console.log(error);
        setNotificationRequestPending(false);
      });
  };

  // load the current user or the selected user from member search
  React.useEffect(() => {
    let loadFunc: any = null;
    if (location.pathname.indexOf("current-user") != -1) {
      loadFunc = loadCurrentUser;
    } else if (location.pathname.indexOf("attendee") != -1) {
      loadFunc = loadAttendeeByAttendeeId;
    } else {
      loadFunc = loadAttendee;
    }
    setLoadAttendeePending(true);
    loadFunc(getIdFromUrl())
      .then((resp: any) => {
        const { data } = resp;
        setIsNewReservation(data.attendeeId === 0 ? true : false);
        // Only default to current user's email if they are registering themselves (current-user path)
        if(data.contactEmail == "" && location.pathname.indexOf("current-user") != -1) {
          data.contactEmail = email;
        }
        // Only call notification API if attendeeId exists and is not 0
        if(data.attendeeId && data.attendeeId !== 0) {
          submitRequestForNotifications(data.attendeeId);
        }
        originalIsCertified.current = data.isCertified;
        parentIsCertified.current = data.parentIsCertified ?? false;
        // Default to home affiliate when no rep affiliate is saved yet
        if (!data.repAffiliateGuid || data.repAffiliateGuid === "00000000-0000-0000-0000-000000000000") {
          data.repAffiliateGuid = data.homeAffiliateGuid;
        }
        // When a certified parent affiliate is available and the rep radio will be
        // shown (affiliateTypeId == 2), elevate isCertified so ConventionRegistrationForm
        // doesn't open the "Not Certified" modal when the user changes the credential
        // type to Delegate before clicking the radio. The submit guard still enforces
        // the correct effective certification via originalIsCertified/parentIsCertified refs.
        if (parentIsCertified.current && data.parentAffiliate?.affiliateTypeId == 2) {
          data.isCertified = true;
        }
        setAttendeeRecord(data);
        saveAttendeeForUserCard(data);
        setLoadAttendeeSuccess(data);
        if(data.credentialTypeId=='13'){
          setIsGuestRecord(true);
        }
      })
      .catch((error: any) => {
        console.log(error);
        setLoadAttendeeError(error);
        setAttendeeLoadFailed(true);
        if (error.response?.status === 404 && error.response?.data?.message) {
          enqueueSnackbar(error.response.data.message, { variant: "error" });
        }
      });
  }, []);

  const getIdFromUrl = () => {
    if(location.pathname.indexOf("member")!=-1){
      const { affiliateGuid, individualGuid } = params as any;
      return `${affiliateGuid}/${individualGuid}`
    }
    return location.pathname.split("/")[3];
  };

  const [
    isCredentialTypeRequestPending,
    setIsCredentialTypeRequestPending,
  ] = React.useState(false);

  // get all of the credential types
  React.useEffect(() => {
    if (creds.length == 0) {
      setIsCredentialTypeRequestPending(true);
      getAllCredentialTypes()
        .then((response) => {
          if (response.data) {
            const { data } = response;
            setCredentialTypes(data);
            setIsCredentialTypeRequestPending(false);
          }
        })
        .catch((error) => {
          console.log(error);
          setIsCredentialTypeRequestPending(false);
        });
    }
  }, []);

  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);

  const handleFormSubmit = (ev: React.SyntheticEvent<HTMLFormElement>) => {
    if(ev) ev.preventDefault();

    // Guard: block submission if credential type requires certification and affiliate is not certified.
    // Derive effective certification from refs (same logic as handleFormChange) rather than
    // attendeeRecord.isCertified, which may still reflect the home affiliate's status when the
    // rep local was pre-populated at load time without firing handleFormChange.
    const requiresCertification =
      selectedCredentialType === "Alternate" ||
      selectedCredentialType === "Ranking Delegate" ||
      selectedCredentialType === "Delegate";

    const hasParentAffiliate = attendeeRecord.parentAffiliate != null;

    const parentIsExplicitlySelected =
      hasParentAffiliate &&
      attendeeRecord.repAffiliateGuid === attendeeRecord.parentAffiliate?.affiliateGuid;

    const effectiveIsCertified = parentIsExplicitlySelected
      ? parentIsCertified.current
      : originalIsCertified.current;

    if (requiresCertification && !effectiveIsCertified) {
      // Open the "Not Certified" modal by setting isCertified = false on the record.
      // ConventionRegistrationForm has a useEffect watching isCertified that opens
      // the modal and disables the form automatically.
      // Use a unique object each time so ConventionRegistrationForm's submissionError
      // useEffect always fires and resets isSubmitting (clears the spinner), even on
      // repeated submission attempts.
      setSaveAttendeeError({ code: "not-certified", ts: Date.now() });
      setAttendeeRecord({ ...attendeeRecord, isCertified: false });
      return;
    }

    setSaveAttendeePending(true);
    saveAttendee(attendeeRecord).then(
      (response) => {
        const { data } = response;

        setIsRegisteredForConvention(true);
        setSaveAttendeeSuccess(data.result);
        // Enrich the data with credential type label and update global store
        const enrichedData = saveAttendeeForUserCard(data.result);
        setAttendeeRecord(enrichedData);
        enqueueSnackbar(data.messages.join(" "), { variant: "success" });
        console.log('handleFormSubmit - ' + attendeeRecord.confirmationNumber)
        console.log('handleFormSubmit - ' + attendeeRecord.inviteeStatus)
        
        // Trigger success view
        setShowRegistrationSuccess(true);
      },
      (error) => {
        console.log(error);
        setSaveAttendeeError(error);
        enqueueSnackbar("Error: " + error.response.data.messages, {
          variant: "error",
        });
      }
    );
  };

  // This one is binded to the input fields
  const [attendeeRecord, setAttendeeRecord] = useState<any>({
    firstName: "",
    lastName: "",
    contactEmail: "",
    ccEmail:"",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    stateCode: "",
    zip: "",
    localNumber: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    committeeChoice1: "",
    committeeChoice2: "",
    committeeChoice3: "",
    confirmationNumber: "",
    inviteeStatus: "",
    alternatePriority: "",
    credentialTypeId: "",
    repAffiliateGuid: "",
    homeAffiliateGuid:"",
    individualGuid:"",
    retireeAttendee: false,
    attendeeId: 0,
    affiliate: null,
    parentAffiliate: null,
    memberId: "",
    interpretorNeeded: false,
    interpretorLanguage: "",
    reportedDate: null,
    createdDate: null,
    modifiedDate: null,
    isCertified: true
  });

  // Use global attendee record instead of local state for savedAttendeeRecord to get credential type updates
  const savedAttendeeRecord = useAppStore((state) => state.attendee?.attendeeRecord) || {
    ...attendeeRecord,
  };

  const [
    conventionRegistrationErrors,
    setConventionRegistrationErrors,
  ] = React.useState({
    phone: { error: false },
    emergencyContactPhone: { error: false },
    zip: { error: false },
    contactEmail: { error: false },
    ccEmail : {error:false}
  });

  const handleFormChange = (ev: any) => {
    let newState = { ...attendeeRecord };

    const { name, value, checked, type } = ev.target;

    if (type === "checkbox") {
      newState[name] = checked;

      // clear dropdown if it is the interpretor checkbox
      if (name === "interpretorNeeded" && checked === false) {
        newState["InterpretorLanguage"] = "";
      }

      setAttendeeRecord({
        ...newState,
      });
      return;
    } else if (type === "text") {
      let pattern: RegExp | null = null;

      if (name === "phone" || name === "emergencyContactPhone") {
        pattern = /^\d{10}$/g;
      }
      if (name === "zip") {
        pattern = /^\d{5}$/g;
      }
      if (name === "contactEmail") {
        pattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,63})+$/;
      }
      if (name === "ccEmail") {
        pattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,63})+$/;
      }

      let isError = false;
      if (name === "phone" || name === "emergencyContactPhone") {
        if (pattern && !value.match(pattern)) {
          if (value.length !== 0) {
            isError = true;
          }
        }
      } else {
        isError = pattern ? !value.match(pattern) : false;
      }

      setConventionRegistrationErrors({
        ...conventionRegistrationErrors,
        [name]: { error: isError },
      });
    }

    // Do not mutate isCertified when the rep affiliate radio changes —
    // ConventionRegistrationForm watches isCertified and would open the
    // "Not Certified" modal mid-edit. The submit guard derives effective
    // certification from refs at submit time instead.

    newState[ev.target.name] = ev.target.value;
    setAttendeeRecord({
      ...newState,
    });
  };

  const originalIsCertified = useRef<boolean>(true);
  const parentIsCertified = useRef<boolean>(false);

  const [selectedCredentialType, setSelectedCredentialType] = React.useState("");

  // Handle dropdown change
  const handleCredentialTypeDropdownChange = (event: any) => {
    const selectedText = event.target.options[event.target.selectedIndex].text;
    setSelectedCredentialType(selectedText);
  };

  // Initialize selectedCredentialType when attendeeRecord loads.
  // Also updates the store so the UserInformation panel shows the correct credential type —
  // needed because saveAttendeeForUserCard runs before creds are fetched on initial load.
  React.useEffect(() => {
    if (attendeeRecord.credentialTypeId && creds.length > 0) {
      const credType = _.find(creds, (cred: any) => {
        return String(cred.credentialTypeId) === String(attendeeRecord.credentialTypeId);
      });
      if (credType) {
        setSelectedCredentialType((credType as any).label);
        setLoadAttendeeSuccess({ ...savedAttendeeRecord, credentialType: (credType as any).label });
      }
    }
  }, [attendeeRecord.credentialTypeId, creds]);

  React.useEffect(() => {
    axios.get("/api/Committees/committeeTypes").then((response: any) => {
      setCommitteeTypes(response.data);
    });
  }, []);

  const additionalFields = () => {
    if (isAdmin) {
      if (attendeeRecord.credentialTypeId) {
        switch (attendeeRecord.credentialTypeId) {
          case "8":
          case "7":
            return (
              <CommitteeSelect
                attendeeRecord={attendeeRecord}
                committeeTypes={committeeTypes as { committeeTypeId: string; committeeName: string; isSpecialCommittee?: boolean }[]}
                handleFormChange={handleFormChange}
              />
            );
          case "9":
            return (
              <AlternatePriority
                attendeeRecord={attendeeRecord}
                handleFormChange={handleFormChange}
              />
            );
          case "11":

          default:
            return "";
        }
      }
    }
    return "";
  };

  const credentialsDisplay = () => {

    if (attendeeRecord.credentialTypeId === "") {
      if (isAffiliateStaff) {
        setAttendeeRecord({...attendeeRecord, credentialTypeId:"11" });
      } else {
        setAttendeeRecord({...attendeeRecord, credentialTypeId:"10" });
      }
    }

    if (isAdmin && !isGuestRecord) {
      return (
        <Dropdown
          id={"delegate-registration-credential-type"}
          values={
            creds.filter(
              (x: any) => x.conventionId == activeConvention?.conventionId &&
              // These credential types do not exist in cvent or have been marked for non-use,
              // so we are filtering them out to avoid confusion.
              x.label !== 'Do Not Use' &&
              x.label !== 'Duplicate' &&
              x.label !== 'Erroneous' &&
              x.label !== 'NonMember' &&
              (isFullAdmin || x.assignableByAffPres)
            ).map((cred: any, index: number) => {
              return (
                <option key={index} value={cred.credentialTypeId}>
                  {cred.label}
                </option>
              );
            })
          }
          required={true}
          selectedPropsValue={
            attendeeRecord.credentialTypeId
          }
          onChange={(event: any) => {
            handleFormChange(event);
            handleCredentialTypeDropdownChange(event);
          }}          
          label={"Credential Type"}
          name={"credentialTypeId"}
        />
      );
    }
    var credential = _.find(creds, (cred: any) => {
      return cred.credentialTypeId == attendeeRecord.credentialTypeId;
    });
    if (credential) {
      return <div>Credential Type: {(credential as any).label}</div>;
    }
  };

  const repRadioDisplay = () => {
    if (
      !!_.get(attendeeRecord, "affiliate") && //is affiliate present & a value
      !!_.get(attendeeRecord, "parentAffiliate") && //is parentAffiliate present && a value
      _.get(attendeeRecord, "parentAffiliate.affiliateTypeId") == 2 //is the parent type state fed
    ) {
        return (
          <FormControl component="fieldset" required>
            <FormLabel component="legend">Represented Affiliate</FormLabel>
            <RadioGroup
              aria-label="position"
              value={attendeeRecord.repAffiliateGuid}
              onChange={handleFormChange}
              name="repAffiliateGuid"
              row
            >
              <FormControlLabel
                key={attendeeRecord.affiliate.affiliateId}
                value={attendeeRecord.affiliate.affiliateGuid}
                control={<Radio color="primary" />}
                label={
                  attendeeRecord.affiliate.affiliateNumber +
                  " - " +
                  attendeeRecord.affiliate.affiliateName
                }
                labelPlacement="end"
              />
              <FormControlLabel
                key={attendeeRecord.parentAffiliate.affiliateId}
                value={attendeeRecord.parentAffiliate.affiliateGuid}
                control={<Radio color="primary" />}
                label={
                  attendeeRecord.parentAffiliate.affiliateNumber +
                  " - " +
                  attendeeRecord.parentAffiliate.affiliateName
                }
                labelPlacement="end"
              />
            </RadioGroup>
          </FormControl>
        );
    }
    return null;
  };

  return (
    <div>
      {
        <div>
          {(attendeeLoadPending || attendeeSavePending) && (
            <div style={regloadingStyle}>
              <GenericSpinner includeOverlay={true} padding={25} />
            </div>
          )}
          {!attendeeLoadFailed && <ConventionRegistrationForm
            conventionRegistrationFormErrors={conventionRegistrationErrors}
            handleFormChange={handleFormChange}
            handleFormSubmit={handleFormSubmit}
            additionalFields={additionalFields()}
            credentialsDisplay={credentialsDisplay()}
            attendeeRecord={attendeeRecord}
            savedAttendeeRecord={savedAttendeeRecord}
            repRadioDisplay={repRadioDisplay()}
            isNewReservation={isNewReservation}
            notificationRequestPending={notificationRequestPending}
            isRegisteredForConvention={isRegisteredForConvention}
            hasHotelReservation={hasHotelReservation}
            isCredentialTypeRequestPending={isCredentialTypeRequestPending}
            ShouldCertifiedPopupShowBasedOnCertificationType={
              selectedCredentialType == "Alternate" ||
              selectedCredentialType == "Ranking Delegate" ||
              selectedCredentialType == "Delegate"
            }
            showSuccessView={showRegistrationSuccess}
            submissionError={saveAttendeeError}
            onEditRegistration={() => setShowRegistrationSuccess(false)}
            onCancelEdit={() => {
              setAttendeeRecord({ ...savedAttendeeRecord });
              setShowRegistrationSuccess(true);
            }}
          />}
        </div>
      }
    </div>
  );
};

export default DelegateRegistration;
